// @flow
import * as React from 'react';
import styled, { type StyledComponent } from 'styled-components';
import { TextField } from '@material-ui/core';
import useMuiFieldTheme from '../text_input/material_ui_theme';
import {
  DangerHelperText,
  HelperText,
} from '../../../internal/internal-typography';
import textInputTheme from '../text_input/theme';
import type { DataProvidesProps } from '../../../utils/constants';
import { getDataProvidesProps } from '../../../utils/constants';
import BaseFlyout from './flyout';
import type { UseSingleDropdownProps } from './use_single_dropdown';
import EndAdornment from './end_adornment';

const FieldWrapper: StyledComponent<*, *, *> = styled.div`
  min-width: 300px;
`;

const SingleDropdown = ({ children }: { children: React.Node }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  return (
    <FieldWrapper ref={setAnchorEl}>
      {React.Children.map(children, (child) =>
        React.cloneElement(child, { anchorEl })
      )}
    </FieldWrapper>
  );
};
SingleDropdown.displayName = 'SingleDropdown';

type SingleDropdownFieldProps = {
  dropdownProps: UseSingleDropdownProps,
  label: string,
  helperText?: string,
  error?: boolean,
  required?: boolean,
  disabled?: boolean,
  readOnly?: boolean,
  fullWidth?: boolean,
  ...DataProvidesProps,
};

SingleDropdown.Field = ({
  dropdownProps,
  ...props
}: SingleDropdownFieldProps) => {
  const {
    getLabelProps,
    getComboboxProps,
    getInputProps,
    openMenu,
    toggleMenu,
    selectedItems,
    isOpen,
    reset,
    loadInitialOptions,
  } = dropdownProps;

  const {
    label,
    helperText,
    error = false,
    required = false,
    disabled = false,
    readOnly = false,
    fullWidth = true,
  } = props;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const classes = useMuiFieldTheme(props);
  const HelperTextComponent = !error ? HelperText : DangerHelperText;

  return (
    <div {...getComboboxProps()}>
      <StyledTextField
        {...getDataProvidesProps(props)}
        $isOpen={dropdownProps.isOpen}
        label={label}
        error={error}
        required={required}
        disabled={disabled}
        variant="filled"
        size="medium"
        fullWidth={fullWidth}
        placeholder={dropdownProps.selectedItem?.label}
        helperText={
          helperText && <HelperTextComponent>{helperText}</HelperTextComponent>
        }
        FormHelperTextProps={{ className: classes.helperText }}
        inputProps={{
          className: classes.htmlInput,
          readOnly,
        }}
        InputProps={{
          ...getInputProps({ refKey: 'inputRef' }),
          onFocus: () => {
            if (!isOpen) {
              loadInitialOptions();
              openMenu();
            }
          },
          onClick: () => {
            if (!isOpen) {
              loadInitialOptions();
              openMenu();
            }
          },
          endAdornment: (
            <EndAdornment
              toggleMenu={toggleMenu}
              disabled={disabled}
              selectedItems={selectedItems}
              singleSelect={true}
              reset={reset}
            />
          ),
          classes: {
            root: classes.inputWrapper,
            underline: classes.inputWrapperUnderline,
            adornedStart: classes.adornedStart,
            adornedEnd: classes.adornedEnd,
          },
        }}
        InputLabelProps={{
          ...getLabelProps(),
          classes: {
            root: classes.inputLabel,
            asterisk: classes.requiredAsterisk,
            focused: classes.focusedInputLabel,
          },
          disableAnimation: true,
          shrink: false,
        }}
      />
    </div>
  );
};
SingleDropdown.Field.displayName = 'SingleDropdown.Field';

type SingleDropdownFlyoutProps = {
  dropdownProps: UseSingleDropdownProps,
  anchorEl?: HTMLElement | any | (() => void),
  noResultsComponent?: React.Node,
  ...DataProvidesProps,
};

SingleDropdown.Flyout = (props: SingleDropdownFlyoutProps) => {
  return <BaseFlyout {...props} />;
};
SingleDropdown.Flyout.displayName = 'SingleDropdown.Flyout';

const StyledTextField = styled(TextField)`
  ${({ $isOpen }) =>
    $isOpen &&
    `
  &&&&&&&&&& .MuiInputBase-root {
    border-radius: 4px 4px 0 0;
    border-color: ${textInputTheme.default.borderColor};
  }
  `}
`;

export default SingleDropdown;
